import { React, useState, useEffect, useCallback, useRef } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page,
  BlockStack,
  Text,
  TextField,
  Button,
  InlineGrid,
  Listbox,
  Select,
  InlineStack,
  Spinner,
} from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { formatPlate } from "../../utility/funcs";

function NewServiceOrder() {
  const [downloading, setDownloading] = useState(false);
  const [car, setCar] = useState(undefined);
  const [plateToSearch, setPlateToSearch] = useState("");
  const [plateSearchLoading, setPlateSearchLoading] = useState(false);
  const [carSearchError, setCarSearchError] = useState(undefined);

  // 🔍 Service Provider Search
  const [providerSearchInput, setProviderSearchInput] = useState("");
  const [providerSearchLoading, setProviderSearchLoading] = useState(false);
  const providerSearchTimeoutRef = useRef(null);
  const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState(false);

  // 📋 Provider search results
  const [providerResults, setProviderResults] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);

  // 📍 Provider locations
  const [providerLocations, setProviderLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const providerSearchWrapperRef = useRef(null);

  const [customerName, setCustomerName] = useState("");
  const [dateTime, setDateTime] = useState(undefined);
  const [reasonForService, setReasonForService] = useState("");

  useEffect(() => {
    return () => {
      if (providerSearchTimeoutRef.current) {
        clearTimeout(providerSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        providerSearchWrapperRef.current &&
        !providerSearchWrapperRef.current.contains(event.target)
      ) {
        setIsProviderDropdownOpen(false); // ✅ CLOSE
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePlateSearch = async (e) => {
    e.preventDefault();
    setPlateSearchLoading(true);
    //Search
    const { data } = await supabase
      .from("cars")
      .select(
        "id, plate, vin, year, odometer, model(name, image_url, brand(name))",
      )
      .eq("plate", plateToSearch.toUpperCase())
      .maybeSingle();
    if (data) {
      setCar(data);
      setCarSearchError(undefined);
    } else {
      setCarSearchError("No encontrada");
    }
    setPlateSearchLoading(false);
  };

  const carSearchPlaceholder = () => {
    if (car) {
      return `${car.model.name} (${formatPlate(car.plate)}) | ${car.model.brand.name}`;
    }
    return plateToSearch;
  };

  const clearCarSearch = () => {
    setCar(undefined);
    setPlateToSearch("");
    setCarSearchError(undefined);
  };

  const handleProviderSearchChange = useCallback((value) => {
    setSelectedProvider(null);
    setSelectedLocation(null);
    setProviderLocations([]);
    setProviderResults([]);
    setProviderSearchLoading(true);
    setIsProviderDropdownOpen(true);
    setProviderSearchInput(value);

    // ⛔ Empty input → CLOSE dropdown
    if (!value || value.trim() === "") {
      setProviderResults([]);
      setProviderSearchLoading(false);
      setIsProviderDropdownOpen(false);
      return;
    }

    if (providerSearchTimeoutRef.current) {
      clearTimeout(providerSearchTimeoutRef.current);
    }

    providerSearchTimeoutRef.current = setTimeout(async () => {
      const { data, error } = await supabase.rpc("search_service_providers", {
        search_text: value.trim(),
      });

      if (error) {
        console.error(error);
        setProviderSearchLoading(false);
        return;
      }

      setProviderResults(data ?? []);
      setProviderSearchLoading(false);
    }, 1000);
  }, []);

  const fetchProviderLocations = useCallback(async (providerId) => {
    setLocationsLoading(true);
    const { data, error } = await supabase
      .from("provider_locations")
      .select("id, label, contact_name, phone, city, state, street_1, street_2, cp")
      .eq("provider_id", providerId)
      .order("is_default", { ascending: false });

    if (error) {
      console.error(error);
      setLocationsLoading(false);
      return;
    }

    setProviderLocations(data ?? []);

    // Auto-select if only one location
    if (data && data.length === 1) {
      setSelectedLocation(data[0]);
    } else {
      setSelectedLocation(null);
    }
    setLocationsLoading(false);
  }, []);

  const handleProviderSelect = useCallback(
    (selectedId) => {
      const provider = providerResults.find(
        (p) => String(p.id) === String(selectedId),
      );

      if (!provider) return;

      setSelectedProvider(provider);
      setProviderSearchInput(provider.name);
      setProviderResults([]);
      setProviderSearchLoading(false);
      setIsProviderDropdownOpen(false);

      // Fetch locations for selected provider
      fetchProviderLocations(provider.id);
    },
    [providerResults, fetchProviderLocations],
  );

  const providerOptionsMarkup =
    providerResults.length > 0
      ? providerResults.map((provider) => (
          <Listbox.Option
            key={provider.id}
            value={String(provider.id)}
            selected={provider.id === selectedProvider}
            accessibilityLabel={provider.name}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <strong>{provider.name}</strong>
              <span style={{ fontSize: "0.8em", color: "#666" }}>
                {`${provider.contact_name} | ${provider.city}, ${provider.state}`}
                {provider.phone ? ` · ${provider.phone}` : ""}
              </span>
            </div>
          </Listbox.Option>
        ))
      : null;

  const isReadyToDownload = () => {
    return (
      car !== undefined &&
      selectedProvider !== null &&
      selectedLocation !== null &&
      typeof customerName === "string" &&
      customerName.trim() !== "" &&
      dateTime !== undefined &&
      typeof reasonForService === "string" &&
      reasonForService.trim() !== ""
    );
  };

  function formatSpanishDateTime(isoString) {
    const date = new Date(isoString);

    const datePart = new Intl.DateTimeFormat("es-MX", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);

    const timePart = new Intl.DateTimeFormat("es-MX", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);

    return `${datePart} a las ${timePart}`;
  }

  const downloadDocs = async () => {
    setDownloading(true);
    let url = "https://api.gomoverent.com/api/pdf-engine/index.php";

    const body = {
      template: "orden_de_servicio_cliente",
      output: "file",
      vars: {
        car,
        provider: {
          ...selectedProvider,
          ...selectedLocation,
        },
        // Sucursal (provider location) name, exposed as its own variable so the
        // PDF template can print it directly instead of digging into provider.
        sucursal: selectedLocation?.label || "",
        date_string: formatSpanishDateTime(dateTime),
        rep_name: "Perla Gonzalez",
        rep_phone: "81 3402 9203",
        customer: customerName,
        reason_for_service: reasonForService,
      },
    };

    let options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    };

    await fetch(url, options)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Network response was not ok");
        }
        return res.blob(); // 👈 IMPORTANT
      })
      .then((blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `Orden de Servicio ${car.plate || "sin placa"} - CLIENTE.pdf`; // 👈 your filename
        document.body.appendChild(a);
        a.click();

        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((err) => console.error("error:", err));

    body["template"] = "orden_de_servicio_taller";

    options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    };

    await fetch(url, options)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Network response was not ok");
        }
        return res.blob(); // 👈 IMPORTANT
      })
      .then((blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `Orden de Servicio ${car.plate || "sin placa"} - TALLER.pdf`; // 👈 your filename
        document.body.appendChild(a);
        a.click();

        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((err) => console.error("error:", err));
    setDownloading(false);
  };

  return (
    <AppWrapper>
      <Page
        title="Nueva Orden de Servicio"
        primaryAction={{
          content: "Descargar",
          onAction: downloadDocs,
          loading: downloading,
          disabled: !isReadyToDownload(),
        }}
        backAction={{ url: "/services" }}
      >
        <div
          style={{
            backgroundColor: "var(--p-color-bg-surface)",
            borderRadius: "var(--p-border-radius-300)",
            padding: "16px",
            boxShadow: "var(--p-shadow-200)",
          }}
        >
          <BlockStack gap={200}>
            <InlineGrid gap={200} columns={2}>
              <BlockStack gap={200}>
                <form onSubmit={handlePlateSearch}>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.25rem",
                    }}
                  >
                    <span style={{ flexGrow: 1 }}>
                      <TextField
                        label={car ? "Vehículo ✅" : "Vehículo"}
                        autoComplete="off"
                        placeholder="Buscar por placa"
                        value={carSearchPlaceholder()}
                        onChange={(value) => setPlateToSearch(value)}
                        error={carSearchError}
                        disabled={plateSearchLoading}
                        readOnly={car}
                        clearButton={car}
                        onClearButtonClick={clearCarSearch}
                      />
                    </span>
                    <span
                      style={{
                        marginTop: "auto",
                        aspectRatio: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: 32,
                        height: 32,
                      }}
                    >
                      <Button
                        icon={SearchIcon}
                        submit
                        disabled={plateToSearch === "" || car}
                        loading={plateSearchLoading}
                      />
                    </span>
                  </div>
                </form>
              </BlockStack>
              <div>
                <div
                  style={{ position: "relative", width: "100%" }}
                  ref={providerSearchWrapperRef}
                >
                  <TextField
                    value={providerSearchInput}
                    placeholder="Buscar proveedor"
                    onChange={handleProviderSearchChange}
                    autoComplete="off"
                    label={selectedProvider ? "Taller ✅" : "Taller"}
                  />

                  {isProviderDropdownOpen && (
                    <div
                      style={{
                        position: "absolute",
                        background: "var(--p-color-bg-surface)",
                        border: "1px solid var(--p-color-border)",
                        borderRadius: "var(--p-border-radius-200)",
                        boxShadow: "var(--p-shadow-300)",
                        width: "100%",
                        padding: "0.5rem",
                        marginTop: "0.3rem",
                        cursor: "pointer",
                        zIndex: 1000,
                      }}
                    >
                      <Listbox onSelect={handleProviderSelect}>
                        {/* Loading */}
                        {providerSearchLoading && (
                          <Listbox.Loading accessibilityLabel="Buscando proveedores" />
                        )}

                        {/* Results */}
                        {!providerSearchLoading && providerOptionsMarkup}

                        {/* No results */}
                        {!providerSearchLoading &&
                          providerResults.length === 0 && (
                            <Listbox.Option disabled value="no-results">
                              <Text tone="subdued">
                                No se encontraron proveedores
                              </Text>
                            </Listbox.Option>
                          )}
                      </Listbox>
                    </div>
                  )}
                </div>
              </div>
            </InlineGrid>
            {selectedProvider && providerLocations.length > 1 && (
              <Select
                label={selectedLocation ? "Sucursal ✅" : "Sucursal"}
                placeholder="Seleccionar sucursal"
                options={providerLocations.map((loc) => ({
                  label: `${loc.label} — ${loc.city}, ${loc.state}`,
                  value: String(loc.id),
                }))}
                value={selectedLocation ? String(selectedLocation.id) : ""}
                onChange={(value) => {
                  const loc = providerLocations.find(
                    (l) => String(l.id) === value,
                  );
                  setSelectedLocation(loc || null);
                }}
              />
            )}
            {selectedProvider && locationsLoading && (
              <InlineStack align="center" gap={200}>
                <Spinner size="small" />
                <Text tone="subdued">Cargando sucursales...</Text>
              </InlineStack>
            )}
            <InlineGrid gap={200} columns={2}>
              <TextField
                label="Cliente"
                value={customerName}
                onChange={(value) => setCustomerName(value)}
              />
              <TextField
                label="Fecha y Hora"
                type="datetime-local"
                value={dateTime}
                onChange={(value) => setDateTime(value)}
              />
            </InlineGrid>
            <TextField
              label="Reporte o Falla"
              multiline={4}
              value={reasonForService}
              onChange={(value) => setReasonForService(value)}
            />
          </BlockStack>
        </div>
      </Page>
    </AppWrapper>
  );
}

export default NewServiceOrder;
